import { useOnlineStatus } from "@@/shared/use-online-status";
import type { TranslatableValueFactoryParams } from "@towni/common";
import type * as React from "react";
import { useLocation } from "react-router";
import { ErrorFilterBoundary } from "./shared/error-filter-boundary";
import { ErrorHandlerBoundary } from "./shared/error-handler-boundary";
import { SentryErrorBoundary } from "./shared/sentry-error-boundary";

type Props = {
    children?: React.ReactNode;
};

const MainErrorBoundaries = (props: Props) => {
    const children = props.children;
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isOnline = useOnlineStatus();

    const retryingMessage = ((): TranslatableValueFactoryParams => {
        if (!isOnline)
            return {
                sv: "Du saknar internetuppkoppling. Anslutningen till servern misslyckades. Försöker igen...",
                en: "You are offline. Connection to server failed. Retrying...",
                de: "Du bist offline. Verbindung zum Server fehlgeschlagen. Erneuter Versuch...",
            };
        return {
            sv: "Kan inte ansluta till servern. Kontrollera din internetuppkoppling. Försöker igen...",
            en: "Connection to server failed. Check your internet connection. Retrying...",
            de: "Verbindung zum Server fehlgeschlagen. Überprüfen Sie Ihre Internetverbindung. Erneuter Versuch...",
        };
    })();
    const errorMessageOverride = ((): TranslatableValueFactoryParams => {
        if (!isOnline)
            return {
                sv: "Du saknar internetuppkoppling. Anslutningen till servern misslyckades.",
                en: "You are offline. Connection to server failed.",
                de: "Du bist offline. Verbindung zum Server fehlgeschlagen.",
            };
        return {
            sv: "Kan inte ansluta till servern. Kontrollera din internetuppkoppling.",
            en: "Connection to server failed. Check your internet connection.",
            de: "Verbindung zum Server fehlgeschlagen. Überprüfen Sie Ihre Internetverbindung.",
        };
    })();

    return (
        <SentryErrorBoundary
            beforeCapture={scope => {
                scope.setTag("errorBoundary", "MainRoutes");
                scope.setTag("fatal", true);
            }}>
            <ErrorFilterBoundary
                errorsToRetry={[
                    {
                        name: [
                            "React ErrorBoundary Error",
                            "React ErrorBoundary TypeError",
                            "TypeError",
                        ],
                        message: "Load failed",
                        retryingMessage,
                        errorMessageOverride,
                    },
                    {
                        name: [
                            "React ErrorBoundary Error",
                            "React ErrorBoundary TypeError",
                            "TypeError",
                        ],
                        message: "Failed to fetch",
                        retryingMessage,
                        errorMessageOverride,
                    },
                ]}>
                <ErrorHandlerBoundary
                    whenMessageIncludes={[
                        "Dynamic import of back office failed",
                        // "Failed to fetch dynamically imported module",
                        // "assets/backoffice.routes",
                    ]}
                    onCatch={error => {
                        // Try to reload, but only once
                        // don't get stuck in a loop
                        const params = Array.from(searchParams.entries())
                            .map(([key, value]) => {
                                return `${key}=${value}`;
                            })
                            .join("&");
                        if (!params.includes("rl=true")) {
                            const url =
                                window.location.origin +
                                window.location.pathname;
                            window.location.href = `${url}?${params}&rl=true`;
                            return;
                        }
                        // If we couldn't solve it with a reload
                        // throw the error
                        throw error;
                    }}>
                    {children}
                </ErrorHandlerBoundary>
            </ErrorFilterBoundary>
        </SentryErrorBoundary>
    );
};

export { MainErrorBoundaries };
